import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/base/LanguageSwitcher';

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <a href="#" className="flex items-center space-x-2 sm:space-x-3 cursor-pointer">
              <div className="w-9 h-9 sm:w-12 sm:h-12 bg-cysol-red rounded-lg flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-base sm:text-xl">C</span>
              </div>
              <div>
                <div className="text-lg sm:text-2xl font-bold text-cysol-black">CYSOL</div>
                <div className="text-xs text-gray-500 -mt-0.5 hidden sm:block">{t('home.logo.tagline')}</div>
              </div>
            </a>

            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-6 lg:space-x-8">
              <a href="#services" className="text-gray-700 hover:text-cysol-red transition-colors font-medium text-sm lg:text-base">
                {t('home.nav.services')}
              </a>
              <a href="#about" className="text-gray-700 hover:text-cysol-red transition-colors font-medium text-sm lg:text-base">
                {t('home.nav.about')}
              </a>
              <LanguageSwitcher />
              <Link
                to="/submit-ticket"
                className="px-4 py-2 bg-cysol-red text-white rounded-lg hover:bg-cysol-red-dark transition-colors font-medium whitespace-nowrap text-sm lg:text-base"
              >
                {t('home.nav.contactUs')}
              </Link>
              <Link
                to="/login"
                className="text-xs text-gray-400 hover:text-gray-600 transition-colors whitespace-nowrap"
              >
                {t('home.nav.adminPortal')}
              </Link>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={t('home.toggleMenu')}
            >
              <i className={`text-xl text-gray-700 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 px-4 py-4 space-y-3 shadow-lg">
            <a
              href="#services"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2.5 px-4 text-gray-700 hover:text-cysol-red hover:bg-red-50 rounded-lg transition-colors font-medium"
            >
              {t('home.nav.services')}
            </a>
            <a
              href="#about"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2.5 px-4 text-gray-700 hover:text-cysol-red hover:bg-red-50 rounded-lg transition-colors font-medium"
            >
              {t('home.nav.about')}
            </a>
            <div className="px-4">
              <LanguageSwitcher />
            </div>
            <Link
              to="/submit-ticket"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2.5 px-4 bg-cysol-red text-white rounded-lg hover:bg-cysol-red-dark transition-colors font-medium text-center whitespace-nowrap"
            >
              {t('home.nav.contactUs')}
            </Link>
            <Link
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              className="block py-2 px-4 text-xs text-gray-400 hover:text-gray-600 transition-colors text-center"
            >
              {t('home.nav.adminPortal')}
            </Link>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 lg:pt-40 lg:pb-32 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-48 h-48 sm:w-72 sm:h-72 bg-cysol-red rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-64 h-64 sm:w-96 sm:h-96 bg-cysol-red rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-6 sm:space-y-8 text-center lg:text-left">
              <div className="inline-flex items-center space-x-2 px-4 py-2 bg-red-50 rounded-full">
                <div className="w-2 h-2 bg-cysol-red rounded-full animate-pulse flex-shrink-0"></div>
                <span className="text-xs sm:text-sm font-semibold text-cysol-red">{t('home.hero.badge')}</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-cysol-black leading-tight">
                {t('home.hero.title')}{' '}
                <span className="text-cysol-red">{t('home.hero.titleHighlight')}</span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed">
                {t('home.hero.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start">
                <Link
                  to="/submit-ticket"
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-cysol-red text-white rounded-xl hover:bg-cysol-red-dark transition-all shadow-cysol hover:shadow-cysol-lg font-semibold text-base sm:text-lg whitespace-nowrap"
                >
                  <i className="ri-ticket-line mr-2 text-lg sm:text-xl"></i>
                  {t('home.hero.submitTicket')}
                </Link>
                <Link
                  to="/submit-ticket"
                  className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border-2 border-gray-300 text-gray-700 rounded-xl hover:border-cysol-red hover:text-cysol-red transition-all font-semibold text-base sm:text-lg whitespace-nowrap"
                >
                  <i className="ri-login-box-line mr-2 text-lg sm:text-xl"></i>
                  {t('home.hero.accessPortal')}
                </Link>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 sm:gap-6 pt-6 sm:pt-8 border-t border-gray-200">
                <div className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-cysol-red">16+</div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">{t('home.hero.activePlants')}</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-cysol-red">24/7</div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">{t('home.hero.support')}</div>
                </div>
                <div className="text-center lg:text-left">
                  <div className="text-2xl sm:text-3xl font-bold text-cysol-red">&lt;24h</div>
                  <div className="text-xs sm:text-sm text-gray-600 mt-1">{t('home.hero.responseTime')}</div>
                </div>
              </div>
            </div>

            {/* Right Image/Visual */}
            <div className="relative mt-4 lg:mt-0">
              <div className="relative bg-gradient-to-br from-cysol-red to-cysol-red-dark rounded-2xl p-5 sm:p-8 shadow-2xl mx-auto max-w-sm sm:max-w-none">
                <div className="bg-white rounded-xl p-4 sm:p-6 space-y-3 sm:space-y-4">
                  {/* Mock Ticket Card */}
                  <div className="flex items-center justify-between pb-3 sm:pb-4 border-b border-gray-200">
                    <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className="ri-tools-line text-orange-600 text-base sm:text-lg"></i>
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 text-sm sm:text-base truncate">Ticket #CYS-2847</div>
                        <div className="text-xs sm:text-sm text-gray-500">{t('home.hero.mockTicket.type')}</div>
                      </div>
                    </div>
                    <span className="px-2 sm:px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold whitespace-nowrap ml-2">
                      {t('home.hero.mockTicket.status')}
                    </span>
                  </div>

                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <i className="ri-building-line text-gray-400 text-sm"></i>
                      <span className="text-xs sm:text-sm text-gray-600">{t('home.hero.mockTicket.plant')}</span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <i className="ri-user-line text-gray-400 text-sm"></i>
                      <span className="text-xs sm:text-sm text-gray-600">{t('home.hero.mockTicket.assigned')}</span>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <i className="ri-time-line text-gray-400 text-sm"></i>
                      <span className="text-xs sm:text-sm text-gray-600">{t('home.hero.mockTicket.eta')}</span>
                    </div>
                  </div>

                  <div className="pt-3 sm:pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs sm:text-sm">
                      <span className="text-gray-500">{t('home.hero.mockTicket.progress')}</span>
                      <span className="font-semibold text-gray-900">65%</span>
                    </div>
                    <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-cysol-red rounded-full" style={{ width: '65%' }}></div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-3 -right-3 sm:-top-4 sm:-right-4 w-14 h-14 sm:w-20 sm:h-20 bg-white rounded-xl shadow-lg flex items-center justify-center">
                  <i className="ri-checkbox-circle-fill text-green-500 text-2xl sm:text-3xl"></i>
                </div>
                <div className="absolute -bottom-3 -left-3 sm:-bottom-4 sm:-left-4 w-12 h-12 sm:w-16 sm:h-16 bg-white rounded-lg shadow-lg flex items-center justify-center">
                  <i className="ri-notification-3-fill text-cysol-red text-xl sm:text-2xl"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-cysol-black mb-3 sm:mb-4">
              {t('home.services.title')}
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('home.services.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-8">
            {[
              { icon: 'ri-ticket-2-line', title: t('home.services.ticketMgmt.title'), desc: t('home.services.ticketMgmt.desc') },
              { icon: 'ri-time-line', title: t('home.services.slaTracking.title'), desc: t('home.services.slaTracking.desc') },
              { icon: 'ri-team-line', title: t('home.services.teamCollab.title'), desc: t('home.services.teamCollab.desc') },
              { icon: 'ri-dashboard-line', title: t('home.services.dashboard.title'), desc: t('home.services.dashboard.desc') },
              { icon: 'ri-file-list-3-line', title: t('home.services.auditHistory.title'), desc: t('home.services.auditHistory.desc') },
              { icon: 'ri-notification-3-line', title: t('home.services.smartNotif.title'), desc: t('home.services.smartNotif.desc') },
            ].map((service, index) => (
              <div key={index} className="bg-white border-2 border-gray-200 rounded-2xl p-6 sm:p-8 hover:border-cysol-red hover:shadow-cysol transition-all group cursor-pointer">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-50 rounded-xl flex items-center justify-center mb-4 sm:mb-6 group-hover:bg-cysol-red transition-colors">
                  <i className={`${service.icon} text-xl sm:text-2xl text-cysol-red group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-14 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-cysol-black mb-4 sm:mb-6">
                {t('home.about.title')}
              </h2>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-4 sm:mb-6">
                {t('home.about.p1')}
              </p>
              <p className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6 sm:mb-8">
                {t('home.about.p2')}
              </p>
              <div className="space-y-4">
                {[
                  { title: t('home.about.feature1.title'), desc: t('home.about.feature1.desc') },
                  { title: t('home.about.feature2.title'), desc: t('home.about.feature2.desc') },
                  { title: t('home.about.feature3.title'), desc: t('home.about.feature3.desc') },
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3 sm:space-x-4">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-cysol-red rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-xs sm:text-sm"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base">{item.title}</h4>
                      <p className="text-sm sm:text-base text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative mt-4 lg:mt-0">
              <div className="bg-gradient-to-br from-cysol-red to-cysol-red-dark rounded-2xl p-5 sm:p-8 mx-auto max-w-sm sm:max-w-none">
                <div className="bg-white rounded-xl p-5 sm:p-8">
                  <div className="grid grid-cols-2 gap-4 sm:gap-6">
                    {[
                      { value: '16+', label: t('home.about.stat.activePlants') },
                      { value: '500+', label: t('home.about.stat.ticketsResolved') },
                      { value: '98%', label: t('home.about.stat.clientSatisfaction') },
                      { value: '<24h', label: t('home.about.stat.avgResponse') },
                    ].map((stat, i) => (
                      <div key={i} className="text-center">
                        <div className="text-2xl sm:text-4xl font-bold text-cysol-red mb-1 sm:mb-2">{stat.value}</div>
                        <div className="text-xs sm:text-sm text-gray-600">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 sm:py-20 bg-gradient-to-br from-cysol-black via-cysol-gray-dark to-cysol-gray-medium">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-base sm:text-xl text-gray-300 mb-8 sm:mb-10 max-w-3xl mx-auto">
            {t('home.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/submit-ticket"
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-cysol-red text-white rounded-xl hover:bg-cysol-red-dark transition-all shadow-cysol-lg font-semibold text-base sm:text-lg whitespace-nowrap"
            >
              <i className="ri-ticket-line mr-2 text-lg sm:text-xl"></i>
              {t('home.cta.submitFirst')}
            </Link>
            <Link
              to="/submit-ticket"
              className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-cysol-black rounded-xl hover:bg-gray-100 transition-all font-semibold text-base sm:text-lg whitespace-nowrap"
            >
              <i className="ri-login-box-line mr-2 text-lg sm:text-xl"></i>
              {t('home.cta.accessPortal')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-cysol-black text-white py-10 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div className="sm:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-cysol-red rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg">C</span>
                </div>
                <div className="text-xl font-bold">CYSOL</div>
              </div>
              <p className="text-gray-400 mb-4 max-w-md text-sm sm:text-base">
                {t('home.footer.description')}
              </p>
              <div className="flex space-x-3 sm:space-x-4">
                <a
                  href="https://www.facebook.com/grupocysol"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 sm:w-10 sm:h-10 bg-cysol-gray-dark rounded-lg flex items-center justify-center hover:bg-cysol-red transition-colors"
                >
                  <i className="ri-facebook-fill text-base sm:text-lg"></i>
                </a>
                <a
                  href="https://www.cysol.com.ar"
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-9 h-9 sm:w-10 sm:h-10 bg-cysol-gray-dark rounded-lg flex items-center justify-center hover:bg-cysol-red transition-colors"
                >
                  <i className="ri-global-line text-base sm:text-lg"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t('home.footer.quickLinks')}</h3>
              <ul className="space-y-2">
                {[
                  { href: '#services', label: t('home.footer.services') },
                  { href: '#about', label: t('home.footer.aboutUs') },
                ].map((link) => (
                  <li key={link.href}>
                    <a href={link.href} className="text-gray-400 hover:text-cysol-red transition-colors text-sm sm:text-base">
                      {link.label}
                    </a>
                  </li>
                ))}
                <li>
                  <Link to="/login" className="text-gray-400 hover:text-cysol-red transition-colors text-sm sm:text-base">
                    {t('home.footer.login')}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">{t('home.footer.contact')}</h3>
              <ul className="space-y-2 text-gray-400">
                <li className="flex items-start space-x-2">
                  <i className="ri-phone-line mt-0.5 text-sm sm:text-base flex-shrink-0"></i>
                  <span className="text-sm sm:text-base">+54 11 1234-5678</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-mail-line mt-0.5 text-sm sm:text-base flex-shrink-0"></i>
                  <span className="text-sm sm:text-base">info@cysol.com.ar</span>
                </li>
                <li className="flex items-start space-x-2">
                  <i className="ri-map-pin-line mt-0.5 text-sm sm:text-base flex-shrink-0"></i>
                  <span className="text-sm sm:text-base">Buenos Aires, Argentina</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-cysol-gray-dark pt-6 sm:pt-8 text-center text-gray-400 text-xs sm:text-sm">
            <p>&copy; {new Date().getFullYear()} Cysol. {t('home.footer.rights')} {t('home.footer.tagline')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
